import { ArcgisEndpointConstant, LayerEntity } from '@gishub/domain';
import { BaseNotifier } from '../../../../shared/notifiers/base-notifier';
import { Ref } from '../../../../shared/providers/ref';
import { LayerProvider } from '../../shared/layer/layer-provider';
import { MapProvider } from '../map/map-provider';
import { UbigeoProvider } from '../../shared/layer/ubigeo/ubigeo-provider';
import { FireLayerState } from './fire-layer-state';
import { resolveFireScope } from './fire-scope';
import type { LegendItemValueObject } from '@gishub/domain';

/** Referencia estable para "sin símbolos aún" (evita reescribir el estado). */
const NO_SYMBOLS: LegendItemValueObject[] = [];

const INITIAL_STATE: FireLayerState = {
  expression: '',
  scopeLabel: 'Nacional',
  layerCount: 0,
  active: false,
  symbols: [],
};

/** Capa de incendios localizada en el catálogo, con su visibilidad efectiva. */
interface FireNode {
  node: LayerEntity;
  /** ¿Se está dibujando? Solo vale si la capa y TODO su ancestro se ven. */
  active: boolean;
}

/**
 * FireLayerNotifier — mantiene las capas de incendios forestales acotadas al
 * ámbito elegido en el header.
 *
 * El ámbito es un solo dato (el ubigeo) que afecta a dos sitios a la vez:
 *
 * 1. **El catálogo del TOC** (`LayerEntity.definitionQuery`). Es la fuente de
 *    verdad: el sincronizador del TOC empuja ese campo al mapa cada vez que algo
 *    cambia, y la fábrica de capas lo usa al crearlas. Dejarlo actualizado evita
 *    que un simple encendido/apagado de capa borre el filtro.
 * 2. **Las capas ya dibujadas** (`definitionExpression`), para que el cambio se
 *    vea de inmediato sin esperar a un ciclo de sincronización.
 *
 * Escribir en los dos lados es deliberado: el catálogo garantiza que el filtro
 * sobreviva, y la escritura directa hace que sea inmediato. Mutar el catálogo no
 * cambia la identidad del estado del TOC, así que no se re-dispara su ciclo de
 * sincronización ni hay bucles.
 *
 * Nacional no es una expresión comodín: se limpia el filtro (`undefined`), que
 * es lo que hace que al volver a Nacional se vean todos los registros.
 */
export class FireLayerNotifier extends BaseNotifier<FireLayerState> {
  /**
   * Carga de leyenda ya pedida. Se libera al terminar para poder reintentar
   * en un cambio de estado posterior; sin este guard, cada eval del efecto
   * dispararía una petición nueva.
   */
  private readonly _legendRequested = new Set<string>();

  constructor(ref: Ref) {
    super(ref, INITIAL_STATE);
  }

  private get ubigeo(): UbigeoProvider {
    return this.ref.watch(UbigeoProvider);
  }

  private get layers(): LayerProvider {
    return this.ref.watch(LayerProvider);
  }

  private get map(): MapProvider {
    return this.ref.watch(MapProvider);
  }

  /** URL base del servicio SERFOR: identifica sus capas sin depender del nombre. */
  private static readonly serviceUrl = ArcgisEndpointConstant.INCENDIO_FORESTAL.baseUrl;

  override build(): void {
    // Un único efecto reacciona a las tres entradas: el ámbito elegido, el
    // catálogo del TOC (llega asíncrono y también cambia al encender capas) y el
    // mapa. Reaplicar es idempotente, así que no hace falta distinguir cuál de
    // los tres movió.
    this.listen(() => {
      const layerState = this.layers.state();
      const found = FireLayerNotifier.collect(layerState.layers);
      const map = this.map.state().map;

      const scope = resolveFireScope(this.ubigeo.state());
      const activeNode = found.find((f) => f.active);

      for (const { node } of found) {
        // `null` (y no cadena vacía) para que el TOC lo trate como "sin filtro"
        // igual que a las capas que nunca lo han tenido.
        if (node.definitionQuery !== (scope.expression || null)) {
          node.definitionQuery = scope.expression || null;
        }
      }

      if (map) {
        // La capa viva del mapa: se escribe aquí además del catálogo para que el
        // cambio se vea ya, sin esperar a que el TOC vuelva a sincronizar.
        for (const layer of map.allLayers.toArray()) {
          const live = layer as { url?: string | null; definitionExpression?: string };
          if (!FireLayerNotifier.isFireUrl(live.url)) continue;
          const expression = scope.expression || undefined;
          if (live.definitionExpression !== expression) {
            live.definitionExpression = expression;
          }
        }
      }

      // La leyenda sólo interesa con la capa dibujada: es lo que muestra la
      // misma simbología que ahora mismo se ve en el mapa.
      if (activeNode) this.requestLegend(activeNode.node);

      const symbols = activeNode
        ? layerState.legends[activeNode.node.id] ?? NO_SYMBOLS
        : NO_SYMBOLS;
      const active = !!activeNode;

      const state = this.snapshot();
      if (
        state.expression === scope.expression &&
        state.scopeLabel === scope.label &&
        state.layerCount === found.length &&
        state.active === active &&
        state.symbols === symbols
      ) {
        return; // nada observable cambió: no se toca el estado
      }
      this.updateState((s) => ({
        ...s,
        expression: scope.expression,
        scopeLabel: scope.label,
        layerCount: found.length,
        active,
        symbols,
      }));
    });
  }

  /**
   * Pide la leyenda del servicio para la capa activa (una sola vez) y a
   * posteriori la reparte en el estado. Si falla, la leyenda del mapa degrada
   * a un punto de color por estado en vez de quedarse en blanco.
   */
  private requestLegend(node: LayerEntity): void {
    if (!node.id || this._legendRequested.has(node.id)) return;
    this._legendRequested.add(node.id);
    void this.layers.notifier
      .loadLegend(node)
      .catch(() => undefined)
      .finally(() => this._legendRequested.delete(node.id));
  }

  /** Capas del catálogo que sirven el servicio de incendios forestales. */
  private static collect(layers: LayerEntity[]): FireNode[] {
    const found: FireNode[] = [];
    const walk = (nodes: LayerEntity[], parentVisible: boolean): void => {
      for (const node of nodes) {
        const visible = parentVisible && !!node.visible;
        if (node.layers?.length) walk(node.layers, visible);
        else if (FireLayerNotifier.isFireUrl(node.url)) found.push({ node, active: visible });
      }
    };
    walk(layers ?? [], true);
    return found;
  }

  /**
   * Pertenencia al servicio SERFOR. Se comprueba sobre la URL y no sobre el
   * nombre: renombrar una capa en el catálogo no debe romper su filtrado.
   */
  private static isFireUrl(url?: string | null): boolean {
    return !!url && url.startsWith(FireLayerNotifier.serviceUrl);
  }
}
